(function () {
  var sections = document.querySelectorAll('.oddit-section-main-product');
  if (!sections.length) return;

  sections.forEach(function (section) {
    initSizeSelector(section);
    initSizeGuideLink(section);
    initGalleryMore(section);
    initGalleryVideos(section);
    initGalleryThumbs(section);
    initAddOnVariantSelects(section);
    initAddToCart(section);
    initAddOnRecommendations(section);
    initPatternSwatchDrag(section);
    initPatternSwatchRows(section);
    initReviewsJump(section);
  });

  /**
   * Add to cart, for both the main buy button and the add-on cards' quick add.
   *
   * The cart drawer's own [data-add-to-cart] handler is bound to the drawer
   * element and gated on `drawer.contains(button)`, so it only ever serves the
   * cards rendered inside the drawer — these buttons need their own handler.
   *
   * Delegated on the section so it survives the add-on cards being re-rendered,
   * and so a click on the button's inner label still resolves to the button.
   */
  function initAddToCart(section) {
    var inFlight = false;

    section.addEventListener('click', function (e) {
      var target = e.target;
      if (!(target instanceof Element)) return;

      var button = target.closest('[data-add-to-cart]');
      if (!button || !section.contains(button) || button.disabled) return;

      e.preventDefault();
      if (inFlight) return;

      // Read the id at click time — the add-on variant select rewrites it.
      var variantId = Number(button.getAttribute('data-variant-id'));
      if (!variantId) return;

      inFlight = true;
      setBusy(button);

      addVariantToCart(variantId)
        .then(function (drawerRefreshed) {
          // Both events are skipped on failure, so a stale cart never shows.
          // The drawer's own helper re-renders as part of the add, so asking
          // for a refresh on top of it would just fetch the cart twice.
          if (!drawerRefreshed) document.dispatchEvent(new CustomEvent('cart:refresh'));
          document.dispatchEvent(new CustomEvent('cart:open'));
        })
        .catch(function () {
          // Swallowed: the button returns to its resting state and the shopper
          // can retry. Nothing in this section renders an error slot.
        })
        .then(function () {
          inFlight = false;
          clearBusy(button);
        });
    });
  }

  /**
   * Prefer the drawer's own helper — it POSTs and re-renders the drawer in one
   * step, keeping its cached cart in step. Fall back to a bare POST when the
   * drawer section is not on the page.
   *
   * Resolves true when the drawer already refreshed itself.
   */
  function addVariantToCart(variantId) {
    if (window.OdditCartDrawer && typeof window.OdditCartDrawer.addToCart === 'function') {
      return window.OdditCartDrawer.addToCart(variantId, 1).then(function () {
        return true;
      });
    }

    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ id: variantId, quantity: 1 })
    }).then(function (res) {
      if (!res.ok) throw new Error('Cart request failed');
      return res.json();
    }).then(function () {
      return false;
    });
  }

  /* Mirrors the cart drawer's busy treatment. The width is pinned so swapping
     the label for the spinner (see oddit-interactions.css) cannot collapse the
     button and shift the row it sits in. */
  function setBusy(button) {
    var restWidth = button.getBoundingClientRect().width;
    if (restWidth > 0) button.style.minWidth = restWidth + 'px';
    button.disabled = true;
    button.classList.add('is-loading');
    button.setAttribute('aria-busy', 'true');
  }

  function clearBusy(button) {
    button.disabled = false;
    button.classList.remove('is-loading');
    button.removeAttribute('aria-busy');
    button.style.minWidth = '';
  }

  /**
   * Products with an empty `custom.add_ons` list fall back to the complementary
   * products managed in the Search & Discovery app. Liquid only sees those on a
   * request through the product recommendations route, so the first render emits
   * an empty placeholder and the real block is fetched here.
   *
   * The response is a full render of this section; only the add-ons block is
   * taken from it. No recommendations means no block in the response, in which
   * case the placeholder is dropped rather than left as an empty gap.
   */
  function initAddOnRecommendations(section) {
    var placeholder = section.querySelector('[data-add-ons-url]');
    if (!placeholder) return;

    var url = placeholder.getAttribute('data-add-ons-url');
    if (!url) {
      placeholder.remove();
      return;
    }

    fetch(url, { credentials: 'same-origin' })
      .then(function (res) {
        if (!res.ok) throw new Error('Product recommendations request failed');
        return res.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh =
          doc.querySelector('[data-add-ons]') ||
          doc.querySelector('.oddit-section-main-product__cross-sell');

        // The pending placeholder carries the same hook, so a response that
        // still has nothing to show would otherwise swap one empty div for
        // another and keep the fetch URL live.
        if (!fresh || !fresh.children.length) {
          placeholder.remove();
          return;
        }

        fresh = document.importNode(fresh, true);
        placeholder.replaceWith(fresh);

        // Selects are bound per element, not delegated like the add buttons.
        initAddOnVariantSelects(section);

        // Freshly injected .oddit-reveal nodes are observed by nothing and would
        // sit at opacity 0 forever without this.
        if (window.OdditInteractions && typeof window.OdditInteractions.attach === 'function') {
          window.OdditInteractions.attach(fresh);
        }
      })
      .catch(function () {
        placeholder.remove();
      });
  }

  /**
   * Add-on cards: keep the variant select, the price, and the add button in sync.
   *
   * The card is rendered server-side against the product's first available
   * variant. The add button carries `data-variant-id`, which is what the cart
   * drawer reads on click — so changing the select without rewriting that id
   * would add a different variant than the one on screen.
   */
  function initAddOnVariantSelects(section) {
    var selects = section.querySelectorAll('[data-xsell-variant-select]');
    if (!selects.length) return;

    selects.forEach(function (select) {
      var card = select.closest('[data-xsell-card]');
      if (!card) return;

      var button = card.querySelector('[data-add-to-cart]');
      var price = card.querySelector('[data-xsell-price]');
      var comparePrice = card.querySelector('[data-xsell-compare-price]');
      if (!button) return;

      var label = button.querySelector('[data-button-label]');

      select.addEventListener('change', function () {
        var option = select.options[select.selectedIndex];
        if (!option) return;

        button.setAttribute('data-variant-id', option.value);

        if (price && option.dataset.price) {
          price.textContent = option.dataset.price;
        }

        var optionCompare = option.dataset.comparePrice;
        if (comparePrice) {
          if (optionCompare) {
            comparePrice.textContent = optionCompare;
            comparePrice.hidden = false;
          } else {
            comparePrice.textContent = '';
            comparePrice.hidden = true;
          }
        }
        if (price) {
          price.classList.toggle(
            'oddit-section-main-product__xsell-price-value--sale',
            Boolean(optionCompare)
          );
        }

        // Sold-out variants are selectable, so the button carries the state.
        var available = option.dataset.available === 'true';
        button.disabled = !available;

        if (label) {
          label.textContent = available
            ? button.dataset.addLabel
            : button.dataset.soldOutLabel;
        }
      });
    });
  }

  /**
   * Mobile gallery thumbnails. Clicking one scrolls its slide into view, and
   * scrolling the strip marks the matching thumb current.
   *
   * Thumbs and slides are rendered from the same source in the same order, so a
   * thumb's index in the strip is its slide's index in the scroller. Positions
   * are read from getBoundingClientRect rather than offsetLeft: the slides'
   * offsetParent is the positioned gallery column, not the scroller, so
   * offsetLeft would not be a scroll offset.
   */
  function initGalleryThumbs(section) {
    var scroller = section.querySelector('[data-gallery-scroll]');
    var thumbs = section.querySelectorAll('[data-gallery-thumb]');
    if (!scroller || !thumbs.length) return;

    // Only the rendered slides, not every child. `oddit-core-image` with
    // `preload: true` emits a <link rel="preload"> next to each <img>, so
    // `scroller.children` interleaves zero-box <link>s with the images — which
    // knocked every slide index off by the preload count and left goTo() with a
    // delta of 0 (the images never moved). Non-rendered tags are filtered out so
    // slide i lines up with thumb i again.
    var slides = Array.prototype.filter.call(scroller.children, function (el) {
      return !/^(LINK|SCRIPT|STYLE|META|TEMPLATE|SOURCE)$/.test(el.tagName);
    });
    var prevArrow = section.querySelector('[data-gallery-arrow="prev"]');
    var nextArrow = section.querySelector('[data-gallery-arrow="next"]');
    var current = 0;

    function goTo(index) {
      var slide = slides[index];
      if (!slide) return;

      var delta = slide.getBoundingClientRect().left - scroller.getBoundingClientRect().left;
      var maxLeft = scroller.scrollWidth - scroller.clientWidth;
      var target = Math.max(0, Math.min(Math.round(scroller.scrollLeft + delta), maxLeft));

      // Lift mandatory scroll-snap for the programmatic write, then restore it so a
      // manual swipe still snaps. Set scrollLeft directly rather than via
      // scrollTo({behavior:'smooth'}): native smooth scrolling is dropped on mobile
      // Chrome against a snap container, and a rAF animation stalls in a
      // backgrounded tab. The target is a snap-start position, so the instant write
      // lands cleanly.
      scroller.style.scrollSnapType = 'none';
      scroller.scrollLeft = target;
      window.setTimeout(function () {
        scroller.style.scrollSnapType = '';
      }, 100);

      setCurrent(index);
    }

    function setCurrent(index) {
      current = index;

      thumbs.forEach(function (thumb, i) {
        if (i === index) {
          thumb.setAttribute('aria-current', 'true');
        } else {
          thumb.removeAttribute('aria-current');
        }
      });

      // An arrow with nowhere to go is removed rather than disabled: the design
      // shows a bare image at each end of the strip.
      if (prevArrow) prevArrow.hidden = index <= 0;
      if (nextArrow) nextArrow.hidden = index >= slides.length - 1;
    }

    thumbs.forEach(function (thumb, i) {
      thumb.addEventListener('click', function () {
        goTo(i);
      });
    });

    if (prevArrow) {
      prevArrow.addEventListener('click', function () {
        goTo(current - 1);
      });
    }

    if (nextArrow) {
      nextArrow.addEventListener('click', function () {
        goTo(current + 1);
      });
    }

    // Sets the starting arrow state, so a single-slide gallery gets neither.
    setCurrent(0);

    // Keep the thumbs in step when the strip is swiped or dragged directly.
    var ticking = false;
    scroller.addEventListener(
      'scroll',
      function () {
        if (ticking) return;
        ticking = true;

        window.requestAnimationFrame(function () {
          ticking = false;

          var origin = scroller.getBoundingClientRect().left;
          var nearest = 0;
          var shortest = Infinity;

          for (var i = 0; i < slides.length && i < thumbs.length; i++) {
            var distance = Math.abs(slides[i].getBoundingClientRect().left - origin);
            if (distance < shortest) {
              shortest = distance;
              nearest = i;
            }
          }

          setCurrent(nearest);
        });
      },
      { passive: true }
    );
  }

  /**
   * Click-and-drag scrolling for the pattern swatch row.
   *
   * The row is an `overflow-x: auto` strip, which already drags on touch, so
   * this is gated to fine pointers — a mouse otherwise has no way to scroll it
   * short of a horizontal wheel. Each swatch is an <a> to a sibling product, so
   * the click that ends a drag has to be swallowed or the drag navigates away.
   *
   * Pointer capture is taken lazily, only once the pointer has actually moved
   * past the threshold. Capturing on pointerdown would retarget the subsequent
   * click from the <a> to this row, which kills plain clicks on the swatches.
   */
  function initPatternSwatchDrag(section) {
    var row = section.querySelector('.oddit-section-main-product__pattern-swatches');
    if (!row) return;
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    // Below this a drag reads as a click, not a scroll — a mouse never lands
    // perfectly still, so 0 would swallow legitimate clicks.
    var DRAG_THRESHOLD = 5;

    var pointerId = null;
    var startX = 0;
    var startScroll = 0;
    var travelled = 0;
    var captured = false;

    row.classList.add('oddit-section-main-product__pattern-swatches--draggable');

    row.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'touch' || e.button !== 0) return;
      if (row.scrollWidth <= row.clientWidth) return;

      pointerId = e.pointerId;
      startX = e.clientX;
      startScroll = row.scrollLeft;
      travelled = 0;
      captured = false;
    });

    row.addEventListener('pointermove', function (e) {
      if (pointerId === null || e.pointerId !== pointerId) return;

      var dx = e.clientX - startX;
      travelled = Math.max(travelled, Math.abs(dx));

      // Scroll from the first pixel so the row tracks the pointer exactly, but
      // only grab the pointer once this is unambiguously a drag.
      if (!captured && travelled > DRAG_THRESHOLD) {
        row.setPointerCapture(pointerId);
        captured = true;
        row.classList.add('oddit-section-main-product__pattern-swatches--dragging');
      }

      row.scrollLeft = startScroll - dx;
    });

    function endDrag(e) {
      if (pointerId === null || e.pointerId !== pointerId) return;
      if (captured && row.hasPointerCapture(pointerId)) {
        row.releasePointerCapture(pointerId);
      }
      pointerId = null;
      captured = false;
      row.classList.remove('oddit-section-main-product__pattern-swatches--dragging');
    }

    row.addEventListener('pointerup', endDrag);
    row.addEventListener('pointercancel', endDrag);

    // Firefox starts a native link/image drag on mousedown-and-move, which
    // steals the pointer events mid-scroll.
    row.addEventListener('dragstart', function (e) {
      if (pointerId !== null) e.preventDefault();
    });

    // Capture phase: the swatch's own click listeners must not see this either.
    row.addEventListener(
      'click',
      function (e) {
        if (travelled > DRAG_THRESHOLD) {
          e.preventDefault();
          e.stopPropagation();
        }
        travelled = 0;
      },
      true
    );
  }

  /**
   * "View more" for the wrapped pattern grid.
   *
   * On desktop the swatches lay out in a grid rather than the mobile scroll
   * strip (see the 990px CSS). A product with many patterns would otherwise
   * stack a tall grid above the buy box, so anything past the third row is
   * collapsed behind a toggle (HP-66).
   *
   * The overflow rows are hidden by setting `hidden` on their swatches, which
   * drops them from both layout and the tab order — cleaner than clipping with
   * overflow, which leaves off-screen links focusable. Rows are read from each
   * swatch's offsetTop, so this follows whatever the layout actually produces
   * and recomputes on resize. On the mobile strip every swatch shares one row,
   * so the toggle never appears and every swatch stays visible.
   */
  function initPatternSwatchRows(section) {
    var MAX_ROWS = 3;

    var row = section.querySelector('.oddit-section-main-product__pattern-swatches');
    if (!row) return;
    var toggle = section.querySelector('[data-pattern-toggle]');
    if (!toggle) return;

    var label = toggle.querySelector('[data-pattern-toggle-label]');
    var moreLabel = toggle.getAttribute('data-more-label') || 'View more';
    var lessLabel = toggle.getAttribute('data-less-label') || 'View less';
    var expanded = false;

    function swatches() {
      return Array.prototype.filter.call(row.children, function (el) {
        return el.classList && el.classList.contains('oddit-pattern-swatch');
      });
    }

    function apply() {
      var items = swatches();
      if (!items.length) return;

      // Measure the natural layout with every swatch visible, so a resize that
      // changes the per-row count is read from scratch rather than from a
      // previously-collapsed state.
      items.forEach(function (el) {
        el.hidden = false;
      });

      var tops = [];
      items.forEach(function (el) {
        var top = el.offsetTop;
        if (tops.indexOf(top) === -1) tops.push(top);
      });
      tops.sort(function (a, b) {
        return a - b;
      });

      if (tops.length <= MAX_ROWS) {
        expanded = false;
        toggle.hidden = true;
        toggle.setAttribute('aria-expanded', 'false');
        if (label) label.textContent = moreLabel;
        return;
      }

      toggle.hidden = false;
      toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      if (label) label.textContent = expanded ? lessLabel : moreLabel;

      if (!expanded) {
        var cutoff = tops[MAX_ROWS]; // offsetTop of the first row past the limit
        items.forEach(function (el) {
          if (el.offsetTop >= cutoff) el.hidden = true;
        });
      }
    }

    toggle.addEventListener('click', function () {
      expanded = !expanded;
      apply();
    });

    apply();

    // Re-measure on resize (the per-row count and total rows both shift), and on
    // the breakpoint flip between the mobile strip and the desktop grid.
    var pending = false;
    window.addEventListener('resize', function () {
      if (pending) return;
      pending = true;
      window.requestAnimationFrame(function () {
        pending = false;
        apply();
      });
    });
  }

  /**
   * Gallery videos render as a bare poster behind a play button. The first play
   * swaps in the native controls (which carry the fullscreen button) and starts
   * playback at 30% volume.
   *
   * The desktop grid and the mobile carousel each render their own copy of every
   * video, so only one is ever allowed to play at a time — otherwise the copy
   * hidden by the media query keeps playing audio.
   */
  function initGalleryVideos(section) {
    var wraps = section.querySelectorAll('[data-oddit-video-wrap]');
    if (!wraps.length) return;

    var videos = [];

    wraps.forEach(function (wrap) {
      var video = wrap.querySelector('[data-oddit-video]');
      var trigger = wrap.querySelector('[data-oddit-video-play]');
      if (!video || !trigger) return;

      videos.push(video);
      video.volume = 0.3;

      trigger.addEventListener('click', function () {
        wrap.classList.add('oddit-core-video--playing');
        video.controls = true;

        var started = video.play();
        if (started && started.catch) {
          started.catch(function () {
            // Playback was rejected (blocked or the source failed to load).
            // Put the button back so the tile is not left inert.
            wrap.classList.remove('oddit-core-video--playing');
          });
        }
      });

      video.addEventListener('play', function () {
        videos.forEach(function (other) {
          if (other !== video && !other.paused) {
            other.pause();
          }
        });
      });
    });
  }

  /**
   * Reveal the gallery images held back past the visible-count cap. One-way:
   * once expanded the trigger and its fade overlay are dropped by the CSS.
   */
  function initGalleryMore(section) {
    var col = section.querySelector('[data-gallery-col]');
    var trigger = section.querySelector('[data-gallery-more]');
    if (!col || !trigger) return;

    trigger.addEventListener('click', function () {
      col.classList.remove('oddit-section-main-product__gallery-col--collapsed');
      trigger.setAttribute('aria-expanded', 'true');
    });
  }

  function updateVariantUrl(variantId) {
    if (!window.history || typeof window.history.replaceState !== 'function') return;

    try {
      var url = new URL(window.location.href);
      if (url.searchParams.get('variant') === String(variantId)) return;
      url.searchParams.set('variant', variantId);
      window.history.replaceState({ variantId: Number(variantId) }, '', url.toString());
    } catch (err) {
      return;
    }

    document.dispatchEvent(
      new CustomEvent('variant:change', {
        detail: { variantId: Number(variantId) }
      })
    );
  }

  /**
   * Size selector: toggle the --active state and update the visible size value.
   */
  function initSizeSelector(section) {
    var buttons = section.querySelectorAll('.oddit-section-main-product__option-btn');
    if (!buttons.length) return;

    var valueEl = section.querySelector(
      '.oddit-section-main-product__option-group .oddit-section-main-product__option-label-value'
    );
    var buyButton = section.querySelector('[data-buy-buttons] [data-add-to-cart]');
    var priceEl = section.querySelector('[data-price-block] [data-price]');
    var comparePriceEl = section.querySelector('[data-price-block] [data-compare-price]');

    buttons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        buttons.forEach(function (b) {
          b.classList.remove('oddit-section-main-product__option-btn--active');
          b.setAttribute('aria-pressed', 'false');
        });
        btn.classList.add('oddit-section-main-product__option-btn--active');
        btn.setAttribute('aria-pressed', 'true');
        if (valueEl) {
          valueEl.textContent = btn.textContent.trim();
        }

        // Synchronize every main-product variant reference before emitting
        // variant:change. The preorder app may add a second buy button and
        // rebuild it synchronously when that event fires, so updating only the
        // original button (or updating it after the event) leaves the visible
        // button pointing at the previously selected variant.
        var variantId = btn.getAttribute('data-variant-id');
        if (!variantId) return;

        var available = btn.dataset.available === 'true';

        // Keep the product forms in sync so integrations read the new variant.
        section.querySelectorAll('input[name="id"]').forEach(function (input) {
          input.value = variantId;
          input.setAttribute('value', variantId);
        });

        // This selector deliberately excludes the add-on cards' quick-add
        // buttons, whose variant ids are managed by their own selects.
        section
          .querySelectorAll(
            '.oddit-section-main-product__add-to-cart[data-add-to-cart]'
          )
          .forEach(function (button) {
            button.setAttribute('data-variant-id', variantId);
            button.disabled = !available;
          });

        if (priceEl && btn.dataset.price) {
          priceEl.textContent = btn.dataset.price;
        }

        var comparePrice = btn.dataset.comparePrice;
        if (comparePriceEl) {
          if (comparePrice) {
            comparePriceEl.textContent = comparePrice;
            comparePriceEl.hidden = false;
          } else {
            comparePriceEl.textContent = '';
            comparePriceEl.hidden = true;
          }
        }
        if (priceEl) {
          priceEl.classList.toggle(
            'oddit-section-main-product__price--sale',
            Boolean(comparePrice)
          );
        }

        // The preorder app owns the label of the button it creates. Only the
        // original theme button's label should be rewritten here.
        if (buyButton) {
          var label = buyButton.querySelector('[data-button-label]');
          if (label) {
            label.textContent = available
              ? buyButton.dataset.addLabel
              : buyButton.dataset.soldOutLabel;
          }
        }

        // Emit only after both name="id" and all main buy buttons are current.
        updateVariantUrl(variantId);
      });
    });
  }

  /**
   * Prevent the Size Guide anchor from following a placeholder href.
   */
  function initSizeGuideLink(section) {
    var link = section.querySelector('.oddit-section-main-product__size-guide');
    if (!link) return;
    link.addEventListener('click', function (e) {
      if (link.getAttribute('href') === '#') {
        e.preventDefault();
      }
    });
  }

  function initReviewsJump(section) {
    var link = section.querySelector('[data-reviews-jump]');
    if (!link) return;

    link.addEventListener('click', function (e) {
      var target = document.querySelector('#judgeme_product_reviews');
      if (!target) return;

      e.preventDefault();

      var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      target.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
    });
  }
})();
